import { useState, useEffect } from 'react';
import { apiService } from '../../../../services/api';

interface UseOwnersDataProps {
  projectId: string;
  areaId?: string;
  selectedParcelId: string | null;
  selectedOwnerId?: string | null;
  loadParcels?: () => Promise<void>;
  loadAllParcels?: () => Promise<void>;
  loadOwnerParcels?: () => Promise<void>;
}

export const useOwnersData = ({ projectId, areaId, selectedParcelId, selectedOwnerId, loadParcels, loadAllParcels, loadOwnerParcels }: UseOwnersDataProps) => {
  const [allOwners, setAllOwners] = useState<any[]>([]);
  const [filteredOwners, setFilteredOwners] = useState<any[]>([]);
  const [isLoadingOwners, setIsLoadingOwners] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<any | null>(null);
  const [ownerNotizen, setOwnerNotizen] = useState<string>('');

  // Lade Owners
  const loadOwners = async () => {
    if (!projectId || !areaId) return;

    try {
      setIsLoadingOwners(true);
      const ownersData = await apiService.getOwners(projectId, areaId);
      setAllOwners(ownersData);
    } catch (error: any) {
      if (error.message?.includes('404')) {
        setAllOwners([]);
      } else {
        setAllOwners([]);
      }
    } finally {
      setIsLoadingOwners(false);
    }
  };

  // Filtere Owners basierend auf selectedParcelId
  useEffect(() => {
    const filterOwners = async () => {
      if (selectedParcelId) {
        try {
          const parcelOwners = await apiService.getParcelOwners(projectId, selectedParcelId);
          setFilteredOwners(parcelOwners);
        } catch (error: any) {
          setFilteredOwners([]);
        }
      } else {
        setFilteredOwners(allOwners);
      }
    };

    filterOwners();
  }, [selectedParcelId, projectId, allOwners]);

  // Lade Owners nach dem Laden der Area
  useEffect(() => {
    if (projectId && areaId) {
      loadOwners();
    }
  }, [projectId, areaId]);

  // Aktualisiere selectedOwner basierend auf selectedOwnerId
  useEffect(() => {
    if (selectedOwnerId) {
      // Suche zuerst in filteredOwners, dann in allOwners
      const owner = filteredOwners.find((o) => o.id === selectedOwnerId) ||
        allOwners.find((o) => o.id === selectedOwnerId) || null;
      setSelectedOwner(owner);
      setOwnerNotizen(owner?.notizen || '');
    } else {
      setSelectedOwner(null);
      setOwnerNotizen('');
    }
  }, [selectedOwnerId, filteredOwners, allOwners]);

  // Update Owner Status
  const updateOwnerStatus = async (ownerId: string, status: string) => {
    if (!projectId || !areaId) return;

    try {
      await apiService.updateOwnerStatus(projectId, areaId, ownerId, status);

      // Owners neu laden
      await loadOwners();

      // Parcels neu laden für Status-Update
      if (loadParcels) {
        await loadParcels();
      }

      // Alle Parcels neu laden für Diagramme
      if (loadAllParcels) {
        await loadAllParcels();
      }

      // Owner-spezifische Parcels neu laden (Status-Propagierung)
      if (loadOwnerParcels) {
        await loadOwnerParcels();
      }
    } catch (error: any) {
      throw error;
    }
  };

  // Update Owner Notes
  const updateOwnerNotes = async (ownerId: string, notizen: string) => {
    if (!projectId || !areaId) return;

    try {
      await apiService.updateOwnerNotes(projectId, areaId, ownerId, notizen);

      // Owners neu laden
      await loadOwners();
    } catch (error: any) {
      throw error;
    }
  };

  return {
    filteredOwners,
    allOwners, // Alle Owners (unabhängig von selectedParcelId)
    isLoadingOwners,
    loadOwners,
    selectedOwner,
    ownerNotizen,
    setOwnerNotizen,
    updateOwnerStatus,
    updateOwnerNotes,
  };
};

